import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSession } from '@/store/useSession'
import type { MoodCheckMode } from '@/store/sessionTypes'
import {
  resolvePractitionerSetupDefaults,
  writePractitionerSetupDefaults,
} from '@/utils/practitionerSetupDefaults'
import { writePersistedSessionStartOptions } from '@/utils/sessionStartOptionsStorage'
import styles from '@/styles/sessionFlow.module.css'

// ── Data ───────────────────────────────────────────────────────────────

const MOOD_CHECK_OPTIONS: { id: MoodCheckMode; label: string; description?: string }[] = [
  {
    id: 'together',
    label: 'Yes, together',
    description: 'Both of you do it on the same screen',
  },
  {
    id: 'client-only',
    label: 'Client only',
    description: "Just for your client — you'll see their result",
  },
  {
    id: 'separately',
    label: 'Yes, separately',
    description: 'Each take a moment privately',
  },
  {
    id: 'none',
    label: 'Not today',
  },
]

const INCLUDE_OPTIONS: { id: boolean; label: string; description?: string }[] = [
  { id: true, label: 'Yes, include it' },
  { id: false, label: 'Not today' },
]

// ── Shared ChoiceCard ──────────────────────────────────────────────────

function ChoiceCard({
  label,
  description,
  icon,
  selected,
  onSelect,
}: {
  label: string
  description?: string
  icon?: string
  selected: boolean
  onSelect: () => void
}) {
  return (
    <button type="button" className={styles.card} aria-pressed={selected} onClick={onSelect}>
      <span className={styles.dot}>
        <span className={styles.dotFill} />
      </span>
      {icon && <span className={styles.cardIcon} aria-hidden="true">{icon}</span>}
      <span className={styles.cardLabel}>
        {label}
        {description && (
          <>
            <br />
            <span style={{ opacity: 0.72, fontSize: '0.83em', fontWeight: 300 }}>
              {description}
            </span>
          </>
        )}
      </span>
    </button>
  )
}

// ── Page ───────────────────────────────────────────────────────────────

export default function PractitionerSetupPage() {
  const navigate = useNavigate()
  const { practitioner, setPractitioner } = useSession()
  const [defaultSaved, setDefaultSaved] = useState(false)

  // Load saved defaults once on mount — not on every practitioner update.
  useEffect(() => {
    setPractitioner(resolvePractitionerSetupDefaults())
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only
  }, [])

  useEffect(() => {
    if (!defaultSaved) return
    const t = window.setTimeout(() => setDefaultSaved(false), 2500)
    return () => window.clearTimeout(t)
  }, [defaultSaved])

  function handleSaveAsDefault() {
    writePractitionerSetupDefaults({
      includeWotCheck: practitioner.includeWotCheck,
      includeWarmup: practitioner.includeWarmup,
      moodCheckMode: practitioner.moodCheckMode,
    })
    setDefaultSaved(true)
  }

  return (
    <main className={styles.page}>
      <div className={styles.inner}>

        {/* ── Opening ──────────────────────────────────────────────── */}
        <section className={styles.section}>
          <h1 className={styles.question} style={{ fontSize: 'clamp(1.35rem, 4vw, 1.9rem)' }}>
            Let's get ready.
          </h1>
        </section>

        <div className={styles.setupSections}>
          {/* ── Window of Tolerance ──────────────────────────────────── */}
          <section className={styles.sectionCard} aria-labelledby="q-wot">
            <h2 id="q-wot" className={styles.question}>
              Include a Window of Tolerance check at the start?
            </h2>
            <div className={styles.cardGroup} role="group" aria-labelledby="q-wot">
              {INCLUDE_OPTIONS.map((opt) => (
                <ChoiceCard
                  key={String(opt.id)}
                  label={opt.label}
                  selected={practitioner.includeWotCheck === opt.id}
                  onSelect={() => setPractitioner({ includeWotCheck: opt.id })}
                />
              ))}
            </div>
          </section>

          {/* ── Mood check ───────────────────────────────────────────── */}
          <section className={styles.sectionCard} aria-labelledby="q-mood">
            <h2 id="q-mood" className={styles.question}>
              Include a mood check at the start?
            </h2>
            <div className={styles.cardGroup} role="group" aria-labelledby="q-mood">
              {MOOD_CHECK_OPTIONS.map((opt) => (
                <ChoiceCard
                  key={opt.id}
                  label={opt.label}
                  description={opt.description}
                  selected={practitioner.moodCheckMode === opt.id}
                  onSelect={() => setPractitioner({ moodCheckMode: opt.id })}
                />
              ))}
            </div>
          </section>

          {/* ── Landing practices ────────────────────────────────────── */}
          <section className={styles.sectionCard} aria-labelledby="q-landing">
            <h2 id="q-landing" className={styles.question}>
              Include landing practices at the start?
            </h2>
            <p className={styles.sectionHint}>
              Breathing and grounding landing practices your client can try before the canvas.
            </p>
            <div className={styles.cardGroup} role="group" aria-labelledby="q-landing">
              {INCLUDE_OPTIONS.map((opt) => (
                <ChoiceCard
                  key={String(opt.id)}
                  label={opt.label}
                  selected={practitioner.includeWarmup === opt.id}
                  onSelect={() => setPractitioner({ includeWarmup: opt.id })}
                />
              ))}
            </div>
          </section>
        </div>

      </div>

      {/* ── Begin session ────────────────────────────────────────────── */}
      <footer className={styles.footer}>
        <button
          type="button"
          className={styles.saveDefaultBtn}
          onClick={handleSaveAsDefault}
        >
          Save as my default
        </button>
        {defaultSaved && (
          <p className={styles.defaultSavedNote} role="status" aria-live="polite">
            Saved as your default.
          </p>
        )}
        <p className={styles.signOff}>Have a good session.</p>
        <button
          type="button"
          className={styles.continueBtn}
          onClick={() => {
            writePersistedSessionStartOptions({
              includeWotCheck: practitioner.includeWotCheck,
              includeWarmup: practitioner.includeWarmup,
              moodCheckMode: practitioner.moodCheckMode,
            })
            navigate('/session/consent')
          }}
        >
          Begin session
        </button>
      </footer>
    </main>
  )
}
